import { Link } from 'react-router-dom';
import { useQuoteModal } from '../../context/QuoteModalContext';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import TestimonialsCarousel from '@/components/feature/TestimonialsCarousel';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://popprotents.com';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${siteUrl}/about/#webpage`,
  name: 'About Pop Pro Tents',
  description:
    'Founded in 2022 in Austin, Texas, Pop Pro Tents is the largest stretch tent supplier and event rental company in the US. It designs and manufactures its own stretch tents and canopies, sold worldwide, and runs full-service event rentals nationwide.',
  url: `${siteUrl}/about`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Pop Pro Tents',
    url: siteUrl,
  },
  about: {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Pop Pro Tents',
    url: siteUrl,
    logo: `${siteUrl}/assets/img/ed091f26a7fa.webp`,
    foundingDate: '2022',
    description:
      'Founded in 2022 and headquartered in Austin, Texas, Pop Pro Tents is the largest stretch tent supplier and event rental company in the US. It designs and manufactures its own stretch tents and canopies, sold worldwide, and provides full-service event rentals nationwide.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Austin',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    areaServed: ['United States', 'Worldwide'],
    telephone: '+1-656-347-7380',
    email: 'info@popprotents.com',
    sameAs: [
      'https://www.facebook.com/popprotents/',
      'https://www.instagram.com/popprotents',
      'https://www.linkedin.com/company/popprotents',
    ],
  },
};

// About copy from Danny (approved 2026-07-01), with three edits Jon signed off:
// dropped the "20+ years" claim (company founded 2022); "largest in North America"
// -> "largest in the US"; and kept manufacturing location unstated with no
// "made in America" implication (manufacturing is not US-based).
const VALUES = [
  {
    icon: 'ri-shield-check-line',
    title: 'Engineered & safe',
    body: 'Wind load calculations, fire certificates, and engineered rigging — documentation you can hand to any venue or fire marshal.',
    to: '/engineering-safety',
  },
  {
    icon: 'ri-award-line',
    title: 'Built to last',
    body: 'Premium materials and ISO-certified quality, re-engineered for the demands of the US event market.',
  },
  {
    icon: 'ri-global-line',
    title: 'Nationwide & global',
    body: 'Austin-based, serving events across the whole US and shipping tents worldwide.',
  },
  {
    icon: 'ri-team-line',
    title: 'One team, end to end',
    body: 'From design to the final install, the same team holds the standard the whole way through.',
  },
];

export default function AboutPage() {
  const { openModal } = useQuoteModal();

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[560px] flex items-center justify-center overflow-hidden">
          <img
            src="/assets/img/brand-activations-03.webp"
            alt="Pop Pro Tents stretch tent at an event"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65"></div>
          <div className="relative z-10 text-center w-full px-4">
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-5 tracking-tight leading-tight">
              About Pop Pro Tents
            </h1>
            <p className="text-xl md:text-2xl text-white/85 font-light max-w-3xl mx-auto">
              The premier American stretch tent company — serving festivals and events coast to coast, from our Austin, Texas base.
            </p>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">The Premier American Stretch Tent Company</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-5">
              Founded in 2022, Pop Pro Tents is a proudly American company based in Austin, Texas. We were one of the very first to introduce the US to the unique, dynamic shade structures known as stretch tents — originally importing them from South Africa, where the concept was born.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-5">
              Since then, we haven&apos;t just supplied tents; we&apos;ve evolved them. We&apos;ve invested heavily in innovating and re-engineering our product to cater specifically to the rigorous demands of the US festival and event market. Today, Pop Pro Tents is the largest stretch tent supplier and rental company in the US, trusted to service major festivals and events from coast to coast.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-5">
              We&apos;ve found that truly spectacular events require a cohesive environment that goes beyond the canopy. To meet that need, we also design and install custom décor elements to complete your space — including structural archways, seating, wayfinding lighting, and bespoke signage.
            </p>
            <p className="text-gray-600 text-base leading-relaxed">
              What truly sets us apart is our end-to-end control. We design and manufacture our own structures, and we run our own full-service rentals. Our American team of expert designers and riggers oversees quality at every stage, right through to the final installation — so whether you&apos;re renting a complete environment for a high-profile nationwide event or buying canopies for your own inventory, you get the exact same industry-leading expertise.
            </p>
          </div>
          <div className="max-w-6xl mx-auto px-6 mt-14">
            <img
              src="/assets/img/team-01.webp"
              alt="The Pop Pro Tents crew in front of a stretch tent installation"
              className="w-full aspect-[16/9] object-cover object-top rounded-2xl shadow-lg"
            />
          </div>
        </section>

        <TestimonialsCarousel />

        {/* Why Pop Pro */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-12 text-center">Why Pop Pro Tents</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {VALUES.map((v) => {
                const inner = (
                  <>
                    <div className="w-11 h-11 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center mb-4">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{v.body}</p>
                  </>
                );
                return v.to ? (
                  <Link
                    key={v.title}
                    to={v.to}
                    className="block bg-white rounded-2xl p-6 border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div key={v.title} className="bg-white rounded-2xl p-6 border border-gray-100">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Rooted in Austin */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-6">Rooted in Austin, Texas</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Our US headquarters and warehouse are in Austin, Texas — the hub for our rental fleet, inventory, and nationwide dispatch. Being centrally located keeps turnarounds tight and delivery reliable for clients from coast to coast.
              </p>
            </div>
            <div>
              <img
                src="/assets/img/2bf586601d94.webp"
                alt="Pop Pro Tents stretch tent set up outdoors"
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto text-center px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Let&apos;s build something unforgettable</h2>
            <p className="text-lg text-gray-500 mb-8">
              Tell us about your event or your project and we&apos;ll get back to you within 24 hours.
            </p>
            <button
              type="button"
              onClick={openModal}
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg font-bold text-base transition-colors whitespace-nowrap cursor-pointer"
            >
              Inquire Now
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
